"use client";

import { useEffect, useRef, useState } from "react";
import Image from "next/image";
import { useRouter } from "next/navigation";
import { ArrowRight, BadgeCheck, FileText, Loader2, Search, SearchX } from "lucide-react";
import type { AppLocale } from "@/core/i18n/config";
import { localizeHref } from "@/core/i18n/routing";
import { getPublicHealerProfilePath } from "@/features/healer-profile/domain/services/healer-slug";
import { cn } from "@/shared/lib/utils";
import { useSearch } from "../hooks/use-search";

interface DashboardSearchProps {
  dashboardBasePath?: string;
  locale: AppLocale;
  searchLabel: string;
  searchPlaceholder: string;
  /** Lets the dashboard keep filtering its own cards by the same query. */
  onQueryChange?: (value: string) => void;
}

function initials(name: string): string {
  return name
    .split(" ")
    .map((part) => part[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();
}

export function DashboardSearch({
  dashboardBasePath,
  locale,
  searchLabel,
  searchPlaceholder,
  onQueryChange,
}: DashboardSearchProps) {
  const router = useRouter();
  const isHindi = locale === "hi";
  const containerRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);

  const { query, setQuery, data, loading, hasSearched } = useSearch({
    initialType: "all",
  });

  // Mirror the query out so the dashboard can still filter its cards.
  useEffect(() => {
    onQueryChange?.(query);
  }, [query, onQueryChange]);

  // Close the dropdown on outside click / Escape.
  useEffect(() => {
    function onPointerDown(event: MouseEvent) {
      if (!containerRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    }
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, []);

  const healers = data?.results.healers?.items ?? [];
  const blogs = data?.results.blogs?.items ?? [];
  const totalResults = (data?.results.healers?.total ?? 0) + (data?.results.blogs?.total ?? 0);

  function goToFullSearch() {
    const trimmed = query.trim();
    if (trimmed.length < 2) return;
    setOpen(false);
    const searchPath = dashboardBasePath
      ? `${dashboardBasePath}/search?q=${encodeURIComponent(trimmed)}`
      : `/search?q=${encodeURIComponent(trimmed)}`;
    router.push(localizeHref(locale, searchPath));
  }

  function getHealerHref(healer: (typeof healers)[number]) {
    const profilePath = getPublicHealerProfilePath(healer);

    if (!dashboardBasePath) {
      return localizeHref(locale, profilePath);
    }

    return localizeHref(locale, `${dashboardBasePath}${profilePath}`);
  }

  const showDropdown = open && query.trim().length >= 2;

  return (
    <div ref={containerRef} className="relative flex min-w-0 flex-1 items-center">
      <label className="relative flex w-full items-center">
        <Search className="pointer-events-none absolute left-4 size-4 text-white/65" />
        <input
          aria-label={searchLabel}
          className="h-11 w-full rounded-full border border-white/12 bg-white/10 pr-11 pl-11 text-sm text-white transition outline-none placeholder:text-white/62 focus:border-white/30 focus:bg-white/14"
          onChange={(event) => {
            setQuery(event.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={(event) => {
            if (event.key === "Enter") goToFullSearch();
          }}
          placeholder={searchPlaceholder}
          type="search"
          value={query}
        />
        {loading ? (
          <Loader2 className="absolute right-4 size-4 animate-spin text-white/80" />
        ) : null}
      </label>

      {showDropdown ? (
        <div className="absolute top-[calc(100%+0.5rem)] right-0 left-0 z-30 overflow-hidden rounded-2xl border border-border bg-card text-foreground shadow-[0_24px_70px_rgba(15,23,42,0.18)]">
          {loading && !hasSearched ? (
            <div className="flex items-center gap-2 px-4 py-6 text-sm text-muted-foreground">
              <Loader2 className="size-4 animate-spin" />
              {isHindi ? "खोज रहे हैं..." : "Searching..."}
            </div>
          ) : totalResults === 0 ? (
            <div className="flex flex-col items-center gap-2 px-4 py-8 text-center">
              <SearchX className="size-6 text-muted-foreground" />
              <p className="text-sm text-muted-foreground">
                {isHindi ? `“${query}” के लिए कुछ नहीं मिला` : `No matches for “${query}”`}
              </p>
            </div>
          ) : (
            <div className="max-h-[60vh] overflow-y-auto py-2">
              {healers.length > 0 ? (
                <div className="py-1">
                  <p className="px-4 py-1.5 text-[11px] font-semibold tracking-wide text-muted-foreground uppercase">
                    {isHindi ? "हीलर" : "Healers"}
                  </p>
                  {healers.slice(0, 5).map((healer) => (
                    <button
                      key={healer.id}
                      type="button"
                      onClick={() => {
                        setOpen(false);
                        router.push(getHealerHref(healer));
                      }}
                      className="flex w-full items-center gap-3 px-4 py-2.5 text-left transition hover:bg-muted"
                    >
                      <span className="relative inline-flex size-9 shrink-0 items-center justify-center overflow-hidden rounded-full bg-primary/10 text-xs font-semibold text-primary">
                        {healer.avatarUrl ? (
                          <Image
                            src={healer.avatarUrl}
                            alt={healer.fullName}
                            fill
                            sizes="36px"
                            className="object-cover"
                          />
                        ) : (
                          initials(healer.fullName)
                        )}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="flex items-center gap-1">
                          <span className="truncate text-sm font-medium text-foreground">
                            {healer.fullName}
                          </span>
                          {healer.verificationStatus === "VERIFIED" ? (
                            <BadgeCheck className="size-3.5 shrink-0 fill-primary text-white" />
                          ) : null}
                        </span>
                        {healer.professionalTitle ? (
                          <span className="truncate text-xs text-muted-foreground">
                            {healer.professionalTitle}
                          </span>
                        ) : null}
                      </span>
                    </button>
                  ))}
                </div>
              ) : null}

              {blogs.length > 0 ? (
                <div className="py-1">
                  <p className="px-4 py-1.5 text-[11px] font-semibold tracking-wide text-muted-foreground uppercase">
                    {isHindi ? "लेख" : "Articles"}
                  </p>
                  {blogs.slice(0, 4).map((blog) => (
                    <button
                      key={blog.id}
                      type="button"
                      onClick={() => {
                        setOpen(false);
                        router.push(localizeHref(locale, `/blogs/${blog.slug}`));
                      }}
                      className="flex w-full items-center gap-3 px-4 py-2.5 text-left transition hover:bg-muted"
                    >
                      <span className="inline-flex size-9 shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground">
                        <FileText className="size-4" />
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm font-medium text-foreground">
                          {blog.title}
                        </span>
                        <span className="truncate text-xs text-muted-foreground">
                          {blog.category?.name ?? (isHindi ? "लेख" : "Article")}
                        </span>
                      </span>
                    </button>
                  ))}
                </div>
              ) : null}

              <button
                type="button"
                onClick={goToFullSearch}
                className={cn(
                  "flex w-full items-center justify-between gap-2 border-t border-border px-4 py-3 text-sm font-medium text-primary transition hover:bg-muted",
                )}
              >
                {isHindi ? `“${query}” के सभी परिणाम देखें` : `See all results for “${query}”`}
                <ArrowRight className="size-4" />
              </button>
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
}
